import Image from 'next/image';
import * as React from 'react';
import { getImageUrl } from '@/lib/cdn';

interface ICertificationCardProps extends React.HTMLAttributes<HTMLDivElement> {
  image: string;
  backParagraph: React.ReactNode;
  altText?: string;
  compact?: boolean;
}

const CertificationCard = ({ image, altText, backParagraph, className, compact }: ICertificationCardProps) => (
  <div className={`${compact ? 'h-[200px] sm:h-[260px] md:h-[300px] xl:h-[324px]' : 'h-[260px] sm:h-[300px] md:h-[324px]'} relative cursor-pointer flip-card${className ? ` ${className}` : ''}`}>
    <div className='flip-card-inner border border-slate-200 rounded-[20px] hover:shadow-md'>
      <div className='flip-card-front flex items-center justify-center'>
        <Image
          className='w-full max-w-[180px] h-auto'
          src={image}
          alt={altText || 'G2 award badge'}
          width={180}
          height={180}
        />
      </div>
      {/* mb-mint-800. The card's white 13px body text reads 3.02:1 on mb-mint-700
          (#3EA67E) and 4.73:1 here, which is the next step down the same ramp. */}
      <div className='flip-card-back px-6 pt-16 rounded-[20px]' style={{ backgroundColor: '#308162' }}>
        <Image
          className='w-full max-w-[61px] h-auto'
          src={image}
          alt={altText || 'G2 award badge'}
          width={61}
          height={61}
        />
        <p className='text-base leading-relaxed text-white w-full text-start mt-4'>{backParagraph}</p>
      </div>
    </div>
  </div>
);

const Certification: React.FunctionComponent = () => (
  <section className='certification-section pt-14 md:pt-20'>
    <div className='container-fluid px-4 md:px-7.5 xl:px-25'>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-wrap -mx-3'>
          <div className='w-1/2 xl:w-1/4 px-3 pb-6 xl:pb-0'>
            <CertificationCard
              image={getImageUrl('G2/AIContentCreationPlatforms_BestSupport_QualityOfSupport.png')}
              altText='G2 Best Support award'
              backParagraph={
                <span className='font-light'>
                  Awarded for <span className='font-semibold'>ensuring fast, expert assistance</span> whenever you need it.
                </span>
              }
            />
          </div>
          <div className='w-1/2 xl:w-1/4 px-3 pb-6 xl:pb-0'>
            <CertificationCard
              image={getImageUrl('G2/AIChatbots_EasiestSetup_EaseOfSetup.png')}
              altText='G2 Easiest Setup award'
              backParagraph={
                <span className='font-light'>
                  Awarded for making implementation <span className='font-semibold'>seamless and hassle-free</span>.
                </span>
              }
            />
          </div>
          <div className='w-full pb-6 xl:pb-0 xl:w-1/2 px-3'>
            <div className='rounded-[20px] h-[324px] py-8 px-6 flex flex-col justify-between' style={{ backgroundColor: '#0848E7' }}>
              <div className='flex items-center justify-between gap-2 flex-wrap'>
                <Image
                  className='h-auto'
                  src={getImageUrl('logo/marketbetter_white.png')}
                  alt='marketbetter logo'
                  width={806}
                  height={222}
                  style={{ width: '160px' }}
                />
                <Image
                  className='rounded-full'
                  src={getImageUrl('about/g2-rating.png')}
                  alt='G2 4.9/5 stars rating'
                  width={120}
                  height={44}
                />
              </div>
              <p className='text-white font-medium max-w-[440px]' style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2.25rem)', lineHeight: '1.25' }}>
                Top Performer in 15 Lead Generation Categories
              </p>
            </div>
          </div>
        </div>
        <div className='flex items-stretch flex-wrap -mx-3'>
          <div className='w-1/3 pb-6 xl:pb-0 px-3'>
            <CertificationCard
              compact
              image={getImageUrl('G2/AIChatbots_HighPerformer_HighPerformer.png')}
              altText='G2 High Performer award'
              backParagraph={
                <span>
                  Recognized for delivering <span className='font-semibold'>exceptional results</span> and performance.
                </span>
              }
            />
          </div>
          <div className='w-1/3 pb-6 xl:pb-0 px-3'>
            <CertificationCard
              compact
              image={getImageUrl('G2/ConversationalMarketing_BestEstimatedROI_Roi.png')}
              altText='G2 Best Estimated ROI award'
              backParagraph={
                <span>
                  Awarded for maximizing returns with the <span className='font-semibold'>highest ROI</span> in the industry.
                </span>
              }
            />
          </div>
          <div className='w-1/3 pb-6 xl:pb-0 px-3'>
            <CertificationCard
              compact
              image={getImageUrl('G2/AIChatbots_EasiestToDoBusinessWith_EaseOfDoingBusinessWith.png')}
              altText='G2 Easiest To Do Business With award'
              backParagraph={
                <span>
                  Recognized for <span className='font-semibold'>effortless collaboration</span> and smooth partnerships.
                </span>
              }
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default Certification;
